// Party Favors builder: quantity steppers that write straight to the Shopify cart,
// plus a live piece count and discount-tier summary. See sections/kalamelo-favor-builder.liquid.
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const DEBOUNCE_MS = 400;

class FavorBuilder {
  constructor(root) {
    this.root = root;
    const d = root.dataset;
    this.t1q = +d.t1q; this.t1p = +d.t1p; this.t2q = +d.t2q; this.t2p = +d.t2p;
    this.everydayMin = +d.emin;
    this.limit = +d.limit || 24;
    this.step = +d.step || 24;
    this.labels = parseLabels(d.labels);
    this.copy = JSON.parse(root.querySelector('[data-kx-copy]')?.textContent || '{}');
    this.cards = [...root.querySelectorAll('[data-kx-card]')];
    this.byVariant = new Map(this.cards.map((c) => [c.dataset.variantId, c]));
    this.accepted = new Map(); // variantId -> last qty the cart confirmed
    this.timers = new Map();
    this.chain = Promise.resolve(); // writes run one at a time so a slow response can't overwrite a newer cart
    this.filter = 'all';
    this.shown = this.limit;

    this.applyLabels();
    this.bindCards();
    this.bindPills();
    this.bindMore();
    this.bindDock();
    this.applyVisibility();
    this.refresh();

    // Keep steppers honest when the cart changes elsewhere (drawer, PDP, another tab).
    document.addEventListener(StandardEvents.cartLinesUpdate, (e) => {
      if (e.context === 'kalamelo-favor-builder') return;
      e.promise?.then(() => this.refresh()).catch(() => {});
    });
    window.addEventListener('pageshow', (e) => { if (e.persisted) this.refresh(); });
  }

  label(type) { return this.labels[type] || type || 'Other'; }

  applyLabels() {
    this.root.querySelectorAll('[data-kx-type-label]').forEach((el) => { el.textContent = this.label(el.textContent.trim()); });
  }

  bindCards() {
    for (const card of this.cards) {
      const input = card.querySelector('[data-kx-qty]');
      if (!input) continue;
      const max = card.dataset.max !== undefined ? +card.dataset.max : Infinity;
      const set = (q) => {
        q = Math.max(0, Math.min(Number.isFinite(q) ? Math.floor(q) : 0, max));
        input.value = q;
        this.paintCard(card, q);
        this.queue(card, q);
      };
      card.querySelector('[data-kx-dec]').addEventListener('click', () => set(+input.value - 1));
      card.querySelector('[data-kx-inc]').addEventListener('click', () => {
        if (+input.value >= max) return this.note(card, `Only ${max} available`);
        set(+input.value + 1);
      });
      input.addEventListener('change', () => {
        if (+input.value > max) this.note(card, `Only ${max} available`);
        set(+input.value);
      });
      input.addEventListener('focus', () => input.select());
    }
  }

  bindPills() {
    this.pills = [...this.root.querySelectorAll('[data-kx-filter]')];
    for (const pill of this.pills) {
      pill.addEventListener('click', () => {
        this.filter = pill.dataset.kxFilter;
        this.pills.forEach((p) => p.setAttribute('aria-pressed', String(p === pill)));
        this.applyVisibility();
      });
    }
  }

  bindMore() {
    this.more = this.root.querySelector('[data-kx-more]');
    this.more?.addEventListener('click', () => { this.shown += this.step; this.applyVisibility(); });
  }

  applyVisibility() {
    let n = 0;
    let matching = 0;
    for (const card of this.cards) {
      const match = this.filter === 'all' || card.dataset.filters.split(' ').includes(this.filter);
      if (match) matching++;
      // A filtered view shows every match; "All" pages through the limit.
      const show = match && (this.filter !== 'all' || n < this.shown);
      if (show) n++;
      card.hidden = !show;
    }
    if (this.more) this.more.hidden = this.filter !== 'all' || matching <= this.shown;
  }

  bindDock() {
    this.dock = this.root.querySelector('[data-kx-dock]');
    if (!this.dock || !('IntersectionObserver' in window)) return;
    const grid = this.root.querySelector('.kx-fb__body');
    new IntersectionObserver(([entry]) => {
      this.dock.classList.toggle('is-visible', entry.isIntersecting);
      this.dock.setAttribute('aria-hidden', String(!entry.isIntersecting));
      this.dock.querySelectorAll('a').forEach((a) => (a.tabIndex = entry.isIntersecting ? 0 : -1));
    }).observe(grid);
  }

  queue(card, qty) {
    const id = card.dataset.variantId;
    clearTimeout(this.timers.get(id));
    this.timers.set(id, setTimeout(() => {
      this.chain = this.chain.then(() => this.write(id, qty));
    }, DEBOUNCE_MS));
  }

  async write(variantId, qty) {
    this.timers.delete(variantId);
    this.error('');
    const card = this.byVariant.get(variantId);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.root.dispatchEvent(new CartLinesUpdateEvent({
      action: 'update',
      context: 'kalamelo-favor-builder',
      lines: [{ id: variantId, quantity: qty }],
      promise: deferred.promise,
    }));
    try {
      const sectionIds = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: { [variantId]: qty }, sections: sectionIds.join(',') }),
      });
      const body = await res.json();
      if (!res.ok || body.status) {
        const available = parseAvailable(body.description) ?? parseAvailable(body.message);
        const back = this.accepted.get(variantId) ?? 0;
        this.setInput(card, back);
        this.note(card, available != null ? `Only ${available} available` : 'Couldn’t update this item. Try again.');
        const cart = await this.fetchCart();
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: true, source: 'kalamelo-favor-builder' } });
        this.render(cart);
        return;
      }
      this.note(card, '');
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(body),
        detail: { source: 'kalamelo-favor-builder', items: body.items, itemCount: body.item_count, sections: body.sections, didError: false },
      });
      this.render(body);
    } catch (err) {
      deferred.reject(err);
      this.error('We couldn’t reach your cart. Check your connection and try again.');
    }
  }

  async fetchCart() {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
    return res.json();
  }

  async refresh() {
    try { this.render(await this.fetchCart()); } catch { /* summary stays at its last state */ }
  }

  render(cart) {
    // Steppers mirror the cart, including pieces added from other pages.
    const qtyByVariant = new Map();
    for (const item of cart.items) qtyByVariant.set(String(item.variant_id), (qtyByVariant.get(String(item.variant_id)) || 0) + item.quantity);
    for (const [id, card] of this.byVariant) {
      const q = qtyByVariant.get(id) || 0;
      this.accepted.set(id, q);
      // Don't overwrite a stepper the shopper is still changing; its own write follows.
      if (!this.timers.has(id)) this.setInput(card, q);
    }

    const counted = cart.items.filter((i) => i.product_type !== 'Subscription');
    const pieces = counted.reduce((n, i) => n + i.quantity, 0);
    const byType = new Map();
    for (const i of counted) {
      const k = this.label(i.product_type);
      byType.set(k, (byType.get(k) || 0) + i.quantity);
    }

    this.root.querySelectorAll('[data-kx-pieces]').forEach((el) => (el.textContent = pieces));
    const of = this.root.querySelector('[data-kx-of]');
    if (of) of.textContent = `${Math.min(pieces, this.t2q)} of ${this.t2q} pieces`;
    const pct = Math.min(100, (pieces / this.t2q) * 100);
    this.root.querySelectorAll('[data-kx-fill]').forEach((el) => (el.style.width = `${pct}%`));

    const lines = this.root.querySelector('[data-kx-lines]');
    if (lines) {
      lines.replaceChildren(...[...byType].map(([k, v]) => {
        const li = document.createElement('li');
        const a = document.createElement('span'); a.textContent = k;
        const b = document.createElement('span'); b.textContent = v;
        li.append(a, b);
        return li;
      }));
    }

    const msg = this.message(pieces, cart.items_subtotal_price);
    this.root.querySelectorAll('[data-kx-msg]').forEach((el) => (el.textContent = msg));
  }

  message(pieces, subtotalCents) {
    const c = this.copy;
    if (pieces >= this.t2q) return c.tier2;
    if (pieces >= this.t1q) return fill(c.tier1, this.t2q - pieces);
    if (pieces === 0) return c.empty;
    return fill(subtotalCents >= this.everydayMin ? c.under_everyday : c.under, this.t1q - pieces);
  }

  setInput(card, q) {
    const input = card?.querySelector('[data-kx-qty]');
    if (!input) return;
    input.value = q;
    this.paintCard(card, q);
  }

  paintCard(card, q) {
    card.classList.toggle('is-selected', q > 0);
    const dec = card.querySelector('[data-kx-dec]');
    if (dec) dec.disabled = q <= 0;
    const inc = card.querySelector('[data-kx-inc]');
    if (inc && card.dataset.max !== undefined) inc.disabled = q >= +card.dataset.max;
  }

  note(card, text) {
    const el = card?.querySelector('[data-kx-note]');
    if (el) el.textContent = text;
  }

  error(text) {
    const el = this.root.querySelector('[data-kx-error]');
    if (el) el.textContent = text;
  }
}

function fill(template, n) {
  return (template || '').replaceAll('{n}', String(n)).replaceAll('{pieces}', n === 1 ? 'piece' : 'pieces');
}

function parseAvailable(text) {
  const m = String(text || '').match(/(\d+)/);
  return m ? +m[1] : null;
}

function parseLabels(raw) {
  const out = {};
  for (const pair of String(raw || '').split(',')) {
    const [from, to] = pair.split(':').map((s) => s && s.trim());
    if (from && to) out[from] = to;
  }
  return out;
}

document.querySelectorAll('[data-kx-fb]').forEach((root) => new FavorBuilder(root));

